// The JS twin of src/Netcountable.App/E2EE/SodiumEnvelope.cs (NC1 envelope,
// FORMAT v3 — see XWING.md; v2 is EPOCH-KEYPAIR.md) plus the enrollment
// fingerprint and the device-list / member-list signing-bytes helpers from
// E2EEManager.cs / E2EEModels.cs. Byte-compatibility with C# and Java is the
// contract — verify-vectors.js (format 3) enforces it.
//
// v3 (2026-10-09, XWING.md §3–§4): every X25519 sealed box became X-Wing
// (ML-KEM-768 + X25519, libsodium 1.0.22 `crypto_kem_xwing_*`), so that stored
// ciphertext cannot be opened later by a quantum computer. The epoch key is an
// X-Wing keypair whose SECRET is a 32-byte seed (members keep holding 32 bytes,
// as in v2) and whose public key is 1216 bytes. Each RECORD gets one
// encapsulation: `(kemCt, ss) = crypto_kem_enc(epochPk)`, the record key is
// `k = BLAKE2b-256(key = ss, "nc1/v3/record-key" ‖ 0x00 ‖ canon({accountId,
// deviceId, epoch, recordId}))`, the 1120-byte `kemCt` rides as a binary
// section of the envelope and the AAD carries `keyHash = b64(BLAKE2b-256(kemCt))`
// so both the AEAD tag and the device signature cover it. One `(kemCt, k)` per
// record: an image, its thumbnail and its imageMeta — or the one event
// envelope — share it, with distinct nonces + `field` discriminators. Only
// members hold the epoch seed (sealed to their identity keys with the v3
// sealed box, §3.3); an uploader needs just the epoch public key + its own
// device signing key and can never decrypt anything — including its own uploads.
//
// Pinned to "draft-connolly-cfrg-xwing-kem-11 as implemented by libsodium
// 1.0.22" (XWING.md decision 8): verify-vectors.js freezes libsodium's own
// X-Wing known answers, so a library upgrade that changes X-Wing's output fails
// the suite before it ships. There is no v2 reader anywhere (decision 7).
//
// Every function takes the ready `sodium` instance (browser: window.sodium
// after the two vendor <script> tags; Node: `await import('libsodium-wrappers-sumo')`).
// libsodium-wrappers-sumo >= 0.8.4 is required (crypto_kem_xwing_*).

import { canonicalize, canonicalizeBytes } from './jcs.js';

const MAGIC = new Uint8Array([0x4e, 0x43, 0x31]); // "NC1"
export const FORMAT_VERSION = 3;
const NONCE_BYTES = 24;  // crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
const TAG_BYTES = 16;    // crypto_aead_xchacha20poly1305_ietf_ABYTES
const SIG_BYTES = 64;    // crypto_sign_BYTES
const HASH_BYTES = 32;   // BLAKE2b-256
const RECORD_KEY_BYTES = 32;
export const KEM_PUBLIC_KEY_BYTES = 1216;   // crypto_kem_xwing_PUBLICKEYBYTES
export const KEM_SEED_BYTES = 32;           // crypto_kem_xwing_SEEDBYTES == _SECRETKEYBYTES (the sk IS the seed)
export const KEM_CIPHERTEXT_BYTES = 1120;   // crypto_kem_xwing_CIPHERTEXTBYTES
const KEM_SHARED_SECRET_BYTES = 32;         // crypto_kem_xwing_SHAREDSECRETBYTES
const MAX_HEADER_BYTES = 64 * 1024;

/** KDF labels (§3): `BLAKE2b-256(key = ss, label ‖ 0x00 ‖ canon(context))`. */
export const RECORD_KEY_LABEL = 'nc1/v3/record-key';
export const SEALED_BOX_LABEL = 'nc1/v3/sealed-box';

// ---- base64: standard alphabet WITH padding, matching C# Convert.To/FromBase64String ----
export function b64(sodium, bytes) {
  return sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
}
export function ub64(sodium, str) {
  return sodium.from_base64(str, sodium.base64_variants.ORIGINAL);
}

export function concatBytes(...arrays) {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}

function u32le(n) {
  const buf = new Uint8Array(4);
  new DataView(buf.buffer).setUint32(0, n, /* littleEndian */ true);
  return buf;
}

function assertKem(sodium) {
  if (typeof sodium.crypto_kem_xwing_enc !== 'function') {
    throw new Error('This libsodium build has no crypto_kem_xwing_* — NC1 v3 needs libsodium-wrappers-sumo >= 0.8.4 (libsodium 1.0.22).');
  }
}

// ---- KDF (§3) ---------------------------------------------------------------

/**
 * `BLAKE2b-256(key = ss, label ‖ 0x00 ‖ contextBytes)` — the ONE key-derivation
 * step of v3 (decision 1: keyed BLAKE2b, not HKDF). `contextBytes` is the
 * canonical JSON of the context object.
 */
export function deriveKey(sodium, label, sharedSecret, contextBytes) {
  if (sharedSecret.length !== KEM_SHARED_SECRET_BYTES) throw new Error('KEM shared secret has wrong length.');
  const msg = concatBytes(new TextEncoder().encode(label), new Uint8Array([0x00]), contextBytes);
  return sodium.crypto_generichash(HASH_BYTES, msg, sharedSecret);
}

/** The record-key KDF context (decision 2): canon({accountId, deviceId, epoch, recordId}). */
export function recordKeyContextBytes(ctx) {
  if (!Number.isInteger(ctx.epoch)) throw new Error('Record key context: epoch must be an integer.');
  return canonicalizeBytes({ accountId: ctx.accountId, deviceId: ctx.deviceId, epoch: ctx.epoch, recordId: ctx.recordId });
}

/** `keyHash` = standard base64 of BLAKE2b-256(kemCt) — 44 characters, carried in the AAD. */
export function keyHashOf(sodium, kemCt) {
  return b64(sodium, sodium.crypto_generichash(HASH_BYTES, kemCt));
}

// ---- epoch keypair (X-Wing) -------------------------------------------------

/** Mint an epoch keypair: { seed (32 B — the epoch SECRET), epochPk (1216 B) }. */
export function epochKeypair(sodium) {
  assertKem(sodium);
  const seed = sodium.randombytes_buf(KEM_SEED_BYTES);
  return { seed, epochPk: epochPkFromSeed(sodium, seed) };
}

/** Public half from the 32-byte epoch seed — members derive it after unsealing their ADK row. */
export function epochPkFromSeed(sodium, seed) {
  assertKem(sodium);
  if (seed.length !== KEM_SEED_BYTES) throw new Error('Epoch seed must be 32 bytes.');
  return sodium.crypto_kem_xwing_seed_keypair(seed).publicKey;
}

/**
 * One record key (§3.2): encapsulate to the epoch public key, derive `k` for
 * THIS record, hash the KEM ciphertext. Returns { k, kemCt, keyHash, ctx }.
 * Reuse the same object for every envelope of ONE record (image + thumbnail
 * + imageMeta; the one event envelope); never across records — sealEnvelope
 * refuses an AAD whose record identity differs from `ctx`.
 * @param ctx { accountId, deviceId, epoch, recordId } — the envelope AAD minus `field`
 */
export function newRecordKey(sodium, epochPk, ctx) {
  assertKem(sodium);
  if (epochPk.length !== KEM_PUBLIC_KEY_BYTES) throw new Error('Epoch public key must be 1216 bytes (X-Wing).');
  const ctxBytes = recordKeyContextBytes(ctx);
  const { ciphertext: kemCt, sharedSecret } = sodium.crypto_kem_xwing_enc(epochPk);
  const k = deriveKey(sodium, RECORD_KEY_LABEL, sharedSecret, ctxBytes);
  sodium.memzero(sharedSecret);
  return {
    k, kemCt, keyHash: keyHashOf(sodium, kemCt),
    ctx: { accountId: ctx.accountId, deviceId: ctx.deviceId, epoch: ctx.epoch, recordId: ctx.recordId },
  };
}

// ---- envelope -------------------------------------------------------------

/**
 * aad = { accountId, recordId, deviceId, epoch, field, keyHash } — epoch must
 * be an integer; `keyHash` is base64(BLAKE2b-256(kemCt)) (present in v3 always).
 */
export function aadBytes(aad) {
  return canonicalizeBytes({
    accountId: aad.accountId,
    recordId: aad.recordId,
    deviceId: aad.deviceId,
    epoch: aad.epoch,
    field: aad.field,
    keyHash: aad.keyHash,
  });
}

/** BLAKE2b-32(kemCt ‖ ciphertext ‖ canon(aad)) — the signed digest. */
function signingDigest(sodium, kemCt, ciphertext, aadB) {
  return sodium.crypto_generichash(HASH_BYTES, concatBytes(kemCt, ciphertext, aadB));
}

/**
 * Encrypt + sign into a binary NC1 v3 envelope (Uint8Array):
 * "NC1" ‖ u32-LE headerLen ‖ header ‖ u32-LE kemLen ‖ kemCt ‖ ciphertext.
 * @param aad { accountId, recordId, deviceId, epoch, field } — `keyHash` is filled in here
 * @param recordKey from newRecordKey(); shared by every envelope of this record
 * @param devSignSk this device's Ed25519 secret key (provenance)
 */
export function sealEnvelope(sodium, plaintext, aad, recordKey, devSignSk) {
  if (!recordKey?.k || !recordKey?.kemCt || !recordKey?.keyHash) throw new Error('sealEnvelope needs a record key from newRecordKey()');
  if (recordKey.kemCt.length !== KEM_CIPHERTEXT_BYTES) throw new Error('Record key KEM ciphertext has wrong length.');
  const c = recordKey.ctx;
  if (c && (c.accountId !== aad.accountId || c.deviceId !== aad.deviceId || c.epoch !== aad.epoch || c.recordId !== aad.recordId)) {
    throw new Error('sealEnvelope: the record key was derived for a different record (accountId/deviceId/epoch/recordId).');
  }
  const fullAad = {
    accountId: aad.accountId, recordId: aad.recordId, deviceId: aad.deviceId,
    epoch: aad.epoch, field: aad.field, keyHash: recordKey.keyHash,
  };
  const aadB = aadBytes(fullAad);
  const nonce = sodium.randombytes_buf(NONCE_BYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, aadB, null, nonce, recordKey.k);
  const sig = sodium.crypto_sign_detached(signingDigest(sodium, recordKey.kemCt, ciphertext, aadB), devSignSk);

  const header = {
    v: FORMAT_VERSION,
    aad: fullAad,
    nonce: b64(sodium, nonce),
    sig: b64(sodium, sig),
  };
  const headerBytes = new TextEncoder().encode(canonicalize(header));

  return concatBytes(MAGIC, u32le(headerBytes.length), headerBytes, u32le(recordKey.kemCt.length), recordKey.kemCt, ciphertext);
}

/** Seal a UTF-8 string payload, return standard base64 of the envelope. */
export function sealEnvelopeString(sodium, plaintextStr, aad, recordKey, devSignSk) {
  return b64(sodium, sealEnvelope(sodium, new TextEncoder().encode(plaintextStr), aad, recordKey, devSignSk));
}

/** Split an envelope into { v, aad, nonce, sig, kemCt, ciphertext }. Throws on malformed input. */
export function parseEnvelope(sodium, envelope) {
  if (envelope.length < MAGIC.length + 4) throw new Error('Envelope too short.');
  if (envelope[0] !== MAGIC[0] || envelope[1] !== MAGIC[1] || envelope[2] !== MAGIC[2]) {
    throw new Error('Envelope magic mismatch — not an NC1 envelope.');
  }
  const headerLen = new DataView(envelope.buffer, envelope.byteOffset + MAGIC.length, 4).getUint32(0, true);
  if (headerLen > MAX_HEADER_BYTES || MAGIC.length + 4 + headerLen > envelope.length) {
    throw new Error('Envelope header length out of range.');
  }
  const headerJson = new TextDecoder().decode(envelope.subarray(MAGIC.length + 4, MAGIC.length + 4 + headerLen));
  const header = JSON.parse(headerJson);
  if (header.v !== FORMAT_VERSION) throw new Error(`Unsupported envelope version ${header.v}.`);
  if (!header.aad || typeof header.aad.keyHash !== 'string') throw new Error('Envelope AAD is missing keyHash.');

  const nonce = ub64(sodium, header.nonce);
  const sig = ub64(sodium, header.sig);
  if (nonce.length !== NONCE_BYTES) throw new Error('Envelope nonce has wrong length.');
  if (sig.length !== SIG_BYTES) throw new Error('Envelope signature has wrong length.');
  if (ub64(sodium, header.aad.keyHash).length !== HASH_BYTES) throw new Error('Envelope keyHash has wrong length.');

  const kemLenOff = MAGIC.length + 4 + headerLen;
  if (kemLenOff + 4 > envelope.length) throw new Error('Envelope too short (no KEM section).');
  const kemLen = new DataView(envelope.buffer, envelope.byteOffset + kemLenOff, 4).getUint32(0, true);
  if (kemLen !== KEM_CIPHERTEXT_BYTES) throw new Error(`Envelope KEM ciphertext length ${kemLen} is not ${KEM_CIPHERTEXT_BYTES}.`);
  const kemOff = kemLenOff + 4;
  if (kemOff + kemLen + TAG_BYTES > envelope.length) throw new Error('Envelope too short (KEM ciphertext / AEAD tag).');

  return {
    v: header.v,
    aad: header.aad,
    nonce,
    sig,
    kemCt: envelope.subarray(kemOff, kemOff + kemLen),
    ciphertext: envelope.subarray(kemOff + kemLen),
  };
}

export function parseEnvelopeString(sodium, envelopeB64) {
  return parseEnvelope(sodium, ub64(sodium, envelopeB64));
}

/** Provenance check — no decryption, no key needed. */
export function verifyEnvelope(sodium, parsed, devSignPk) {
  return sodium.crypto_sign_verify_detached(
    parsed.sig, signingDigest(sodium, parsed.kemCt, parsed.ciphertext, aadBytes(parsed.aad)), devSignPk);
}

/**
 * Recover the record key with the epoch SEED (members only). Checks `keyHash`
 * against the carried KEM ciphertext FIRST (a swapped key is a clear error),
 * then decapsulates and derives. ML-KEM decapsulation never fails loudly
 * (implicit rejection): a wrong seed yields a different `k`, and the AEAD tag
 * in decryptEnvelope is the authoritative "cannot open".
 */
export function openRecordKey(sodium, parsed, epochSeed) {
  assertKem(sodium);
  if (epochSeed.length !== KEM_SEED_BYTES) throw new Error('Epoch seed must be 32 bytes.');
  if (parsed.kemCt.length !== KEM_CIPHERTEXT_BYTES) throw new Error('Envelope KEM ciphertext has wrong length.');
  if (keyHashOf(sodium, parsed.kemCt) !== parsed.aad.keyHash) {
    throw new Error('Envelope keyHash does not match its KEM ciphertext (swapped record key).');
  }
  const ss = sodium.crypto_kem_xwing_dec(parsed.kemCt, epochSeed);
  const k = deriveKey(sodium, RECORD_KEY_LABEL, ss, recordKeyContextBytes(parsed.aad));
  sodium.memzero(ss);
  return k;
}

/** AEAD decrypt with the record key. Throws on tag/AAD mismatch. Returns Uint8Array. */
export function decryptEnvelope(sodium, parsed, recordKeyBytes) {
  if (recordKeyBytes.length !== RECORD_KEY_BYTES) throw new Error('Record key has wrong length.');
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null, parsed.ciphertext, aadBytes(parsed.aad), parsed.nonce, recordKeyBytes);
}

// ---- sealed box v3 (§3.3): epoch seed → member, invite keys, device transfer ----

/**
 * Seal a KNOWN plaintext to someone's X-Wing public key:
 *   (ct, ss) = crypto_kem_enc(recipientPk)
 *   key      = BLAKE2b-256(key = ss, "nc1/v3/sealed-box" ‖ 0x00 ‖ canon(context))
 *   sealed   = ct ‖ XChaCha20-Poly1305(key, nonce = 24 zero bytes, plaintext, ad = canon(context))
 * The nonce is zero because the key is fresh per encapsulation. Output is
 * 1120 + len(plaintext) + 16 bytes (an ADK row: 1168).
 * @param context what the row is for — ADK row {accountId, keyEpoch, userId},
 *   invite key {ownerUserId, memberUserId}, transfer {requestId}
 */
export function sealBox(sodium, plaintext, recipientPk, context) {
  assertKem(sodium);
  if (recipientPk.length !== KEM_PUBLIC_KEY_BYTES) throw new Error('Recipient public key must be 1216 bytes (X-Wing).');
  const ctxBytes = canonicalizeBytes(context);
  const { ciphertext: kemCt, sharedSecret } = sodium.crypto_kem_xwing_enc(recipientPk);
  const key = deriveKey(sodium, SEALED_BOX_LABEL, sharedSecret, ctxBytes);
  sodium.memzero(sharedSecret);
  const sealed = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, ctxBytes, null, new Uint8Array(NONCE_BYTES), key);
  sodium.memzero(key);
  return concatBytes(kemCt, sealed);
}

/** Open a v3 sealed box with the recipient's seed. Any failure throws "not sealed to this identity". */
export function openBox(sodium, sealed, recipientSeed, context) {
  assertKem(sodium);
  if (recipientSeed.length !== KEM_SEED_BYTES) throw new Error('Recipient seed must be 32 bytes.');
  if (sealed.length < KEM_CIPHERTEXT_BYTES + TAG_BYTES) throw new Error('Sealed box too short.');
  const ctxBytes = canonicalizeBytes(context);
  const ss = sodium.crypto_kem_xwing_dec(sealed.subarray(0, KEM_CIPHERTEXT_BYTES), recipientSeed);
  const key = deriveKey(sodium, SEALED_BOX_LABEL, ss, ctxBytes);
  sodium.memzero(ss);
  try {
    return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null, sealed.subarray(KEM_CIPHERTEXT_BYTES), ctxBytes, new Uint8Array(NONCE_BYTES), key);
  } catch {
    throw new Error('Sealed box is not sealed to this identity (or was tampered with).');
  } finally {
    sodium.memzero(key);
  }
}

/**
 * Enrollment fingerprint (VIEWER links + safety numbers only — uploader
 * approvals are by device name since v2):
 * hex(first 8 bytes of BLAKE2b-16(transfer_pk ‖ dev_sign_pk ‖ dev_enc_pk)).
 * (BLAKE2b-16 then truncate, because crypto_generichash_BYTES_MIN is 16.)
 */
export function computeFingerprint(sodium, transferPk, devSignPk, devEncPk) {
  const hash = sodium.crypto_generichash(16, concatBytes(transferPk, devSignPk, devEncPk));
  return Array.from(hash.subarray(0, 8), (x) => x.toString(16).padStart(2, '0')).join('');
}

/** XXXX-XXXX-XXXX-XXXX display form. */
export function fingerprintDisplay(fingerprint) {
  return fingerprint.toUpperCase().match(/.{4}/g).join('-');
}

/**
 * §8.3 read-and-verify for one parsed envelope:
 *   1. resolve the author device's signing pk from the (already
 *      identity-verified) device list — unknown device = untrusted
 *   2. verify the device signature over BLAKE2b(kemCt ‖ ciphertext ‖ canon(aad))
 *   3. open the record key with the epoch seed for the envelope's epoch
 *      (keyHash check, decapsulate, derive), then AEAD-decrypt
 * Returns { ok, plaintext?, error?, revoked? } — never throws.
 *
 * @param ctx {
 *   devicePks: Map<deviceId, Uint8Array>,
 *   epochSks: Map<epochNumber, Uint8Array>,   // the caller's unsealed epoch SEEDS
 *   revokedDevices?: Set<deviceId>,
 *   recordKeys?: Map<keyHash, Uint8Array>     // per-load cache: one open per record, not per field
 * }
 */
export function openParsed(sodium, parsed, ctx) {
  const devPk = ctx.devicePks.get(parsed.aad.deviceId);
  if (!devPk) return { ok: false, error: `unknown device ${parsed.aad.deviceId} (not on the signed device list)` };
  if (!verifyEnvelope(sodium, parsed, devPk)) return { ok: false, error: 'BAD PROVENANCE SIGNATURE' };
  const epochSeed = ctx.epochSks?.get(parsed.aad.epoch);
  if (!epochSeed) return { ok: false, error: `no epoch key for epoch ${parsed.aad.epoch}` };
  try {
    if (!ctx.recordKeys) ctx.recordKeys = new Map();
    let k = ctx.recordKeys.get(parsed.aad.keyHash);
    if (!k) {
      k = openRecordKey(sodium, parsed, epochSeed);
      ctx.recordKeys.set(parsed.aad.keyHash, k);
    }
    // Tombstoned author: historical records verify, but surface it — new
    // records from a revoked device are a red flag for the caller/UI.
    const revoked = ctx.revokedDevices?.has(parsed.aad.deviceId) ?? false;
    return { ok: true, plaintext: decryptEnvelope(sodium, parsed, k), revoked };
  } catch {
    return { ok: false, error: 'RECORD KEY OPEN OR AEAD DECRYPT FAILED (wrong epoch key, swapped key, or tag/AAD mismatch)' };
  }
}

/**
 * The exact bytes the identity key signs for a device list:
 * canon({ devices: [{addedAt, devEncPk?, devSignPk, deviceId, revokedAt?}], userId, version }).
 * Mirror of C# SignedDeviceList.SignedBytes() / DeviceListEntry.ToJsonNode().
 */
export function deviceListSignedBytes(list) {
  return canonicalizeBytes({
    userId: list.userId,
    version: list.version,
    devices: list.devices.map((d) => deviceEntryForSigning(d)),
  });
}

/** Canonical shape of one device-list entry (shared by signing + re-signing paths). */
export function deviceEntryForSigning(d) {
  return {
    deviceId: d.deviceId,
    devSignPk: d.devSignPk,
    // v2+ uploaders have no encryption key (nothing is ever sealed to a
    // device). Omitted entirely when absent so the canonical bytes match C#
    // DeviceListEntry.ToJsonNode(); viewer-era / legacy entries may carry it.
    ...(d.devEncPk != null ? { devEncPk: d.devEncPk } : {}),
    addedAt: d.addedAt,
    // Tombstone for revoked devices — omitted entirely when absent.
    ...(d.revokedAt != null ? { revokedAt: d.revokedAt } : {}),
  };
}

/**
 * The exact bytes the OWNER identity key signs for a member list (§7.3) —
 * v2 added `epochPk`, the CURRENT epoch's public key (the only key an
 * uploader needs); in v3 it is the 1216-byte X-Wing key, same shape. Single
 * home for this preimage: the service validator, the webapp and C# all mirror
 * it. `ownerSig` is never part of the bytes.
 */
export function memberListSignedBytes(list) {
  return canonicalizeBytes({
    accountId: list.accountId,
    version: list.version,
    keyEpoch: list.keyEpoch,
    epochPk: list.epochPk,
    members: list.members,
  });
}
